"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import type { Project, ProjectItem } from "@/lib/types";
import { dateSortKey } from "@/lib/data/projects";
import { useAuth } from "@/lib/context/AuthProvider";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useLocalStorageState } from "@/lib/hooks/useLocalStorageState";
import { ApiError, isApiError } from "@/lib/api/client";
import { invalidateProjects } from "@/lib/api/cache";
import {
  fetchProjects,
  mapApiProjectGenerationSummary,
  patchProject,
  storeProject,
  syncGuestProjects,
  type ApiProject,
  type GuestStateSyncRequest,
} from "@/lib/api/projects";
import { slugify } from "@/lib/utils";

/** History screens load generations directly — skip GET /v1/project list prefetch. */
function isHistoryRoute(pathname: string | null): boolean {
  return pathname === "/history" || Boolean(pathname?.startsWith("/history/"));
}

/** Tools list/detail don't need the projects grid — skip list prefetch. */
function isToolsRoute(pathname: string | null): boolean {
  return pathname === "/tools" || Boolean(pathname?.startsWith("/tools/"));
}

/** Certificates page has no project UI — skip list prefetch. */
function isCertificatesRoute(pathname: string | null): boolean {
  return pathname === "/certificates" || Boolean(pathname?.startsWith("/certificates/"));
}

/**
 * Project detail loads via GET /v1/project/{slug} — skip the list prefetch.
 * Does not match /projects, /projects/new, or /projects/:slug/edit.
 */
function isProjectDetailRoute(pathname: string | null): boolean {
  if (!pathname) return false;
  const match = pathname.match(/^\/projects\/([^/]+)$/);
  return Boolean(match && match[1] !== "new");
}

/** Create/edit overlays already have project data from cache or the prior screen. */
function isProjectModalRoute(pathname: string | null): boolean {
  return (
    pathname === "/projects/new" ||
    Boolean(pathname?.match(/^\/projects\/[^/]+\/edit$/))
  );
}

/** Routes that already load what they need without the paginated project list. */
function shouldSkipProjectListPrefetch(pathname: string | null): boolean {
  return (
    isHistoryRoute(pathname) ||
    isToolsRoute(pathname) ||
    isCertificatesRoute(pathname) ||
    isProjectDetailRoute(pathname) ||
    isProjectModalRoute(pathname)
  );
}

/**
 * ─── Backend seam ───────────────────────────────────────────────
 * Authenticated: list/create/update/notes via GET|POST|PATCH /v1/project.
 * Guest: versioned localStorage bag (`psycloud_guest_projects`) with a seeded
 * Default project; tool runs still hit the API without projectSlug, then
 * attach into the local project via `addGeneratedItem`.
 * After JWT (register / login / OAuth / guest checkout): POST
 * /v1/project/guest-sync, then `clearGuestProjects()` on success.
 * The component tree only depends on the returned context shape.
 */

/** Sentinel for generations with no linked project (orphan guest tool runs). */
export const GUEST_PROJECT_ID = "guest";
export const GUEST_PROJECT_SLUG = "guest-drafts";

/** localStorage key for signed-out project bag (migrate-ready). */
export const GUEST_PROJECTS_STORAGE_KEY = "psycloud_guest_projects";
export const GUEST_PROJECTS_STORAGE_VERSION = 1 as const;

export interface GuestProjectRecord {
  id: string;
  slug: string;
  name: string;
  desc: string;
  notes: string;
  items: ProjectItem[];
  createdAt: number;
  updatedAt: number;
}

export interface GuestProjectsStorage {
  version: typeof GUEST_PROJECTS_STORAGE_VERSION;
  projects: GuestProjectRecord[];
}

export const EMPTY_GUEST_PROJECTS_STORAGE: GuestProjectsStorage = {
  version: GUEST_PROJECTS_STORAGE_VERSION,
  projects: [],
};

/** Read the guest bag for account migration (null if missing/invalid). */
export function readGuestProjects(): GuestProjectsStorage | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(GUEST_PROJECTS_STORAGE_KEY);
    if (raw == null) return null;
    const parsed = JSON.parse(raw) as GuestProjectsStorage;
    if (!parsed || !Array.isArray(parsed.projects)) return null;
    return parsed;
  } catch (err) {
    console.error("Failed to read guest projects from localStorage", err);
    return null;
  }
}

/** Clear the guest bag after a successful guest-sync. */
export function clearGuestProjects(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(GUEST_PROJECTS_STORAGE_KEY);
  } catch (err) {
    console.error("Failed to clear guest projects from localStorage", err);
  }
}

const LIST_PER_PAGE = 10;
const CATALOG_PER_PAGE = 100;

/** Backend limits for POST /v1/project/guest-sync. */
const GUEST_SYNC_MAX_PROJECTS = 50;
const GUEST_SYNC_MAX_GENERATIONS_PER_PROJECT = 100;
const GUEST_SYNC_MAX_GENERATIONS_TOTAL = 200;

/**
 * True when the bag has more than an empty seeded Default project.
 * Syncing an untouched seed would overwrite the server `default` project.
 */
export function isGuestBagSyncable(
  bag: GuestProjectsStorage,
  defaultProjectName: string,
): boolean {
  for (const project of bag.projects) {
    if (project.items.length > 0) return true;
    if (project.slug !== "default") return true;
    if (
      project.slug === "default" &&
      (project.name !== defaultProjectName ||
        project.desc.trim().length > 0 ||
        project.notes.trim().length > 0)
    ) {
      return true;
    }
  }
  return false;
}

/** Map the local guest bag onto POST /v1/project/guest-sync. */
export function toGuestSyncPayload(
  bag: GuestProjectsStorage,
): GuestStateSyncRequest {
  return {
    projects: bag.projects.map((project) => ({
      isDefault: project.slug === "default",
      name: project.name,
      description: project.desc || null,
      notes: project.notes || null,
      generations: project.items.map((item) => ({
        slug: item.slug,
        title: item.name,
      })),
    })),
  };
}

/**
 * Client-side guard matching API constraints. Returns an error message
 * for logging, or null when the payload is within limits.
 */
export function validateGuestSyncPayload(
  payload: GuestStateSyncRequest,
): string | null {
  if (payload.projects.length > GUEST_SYNC_MAX_PROJECTS) {
    return `Guest sync exceeds max projects (${GUEST_SYNC_MAX_PROJECTS})`;
  }

  let defaultCount = 0;
  let totalGenerations = 0;
  const seenSlugs = new Set<string>();

  for (const project of payload.projects) {
    if (project.isDefault) defaultCount += 1;
    if (project.generations.length > GUEST_SYNC_MAX_GENERATIONS_PER_PROJECT) {
      return `Guest sync exceeds max generations per project (${GUEST_SYNC_MAX_GENERATIONS_PER_PROJECT})`;
    }
    totalGenerations += project.generations.length;
    for (const generation of project.generations) {
      if (seenSlugs.has(generation.slug)) {
        return `Guest sync has duplicate generation slug: ${generation.slug}`;
      }
      seenSlugs.add(generation.slug);
    }
  }

  if (defaultCount > 1) {
    return "Guest sync payload may include at most one default project";
  }
  if (totalGenerations > GUEST_SYNC_MAX_GENERATIONS_TOTAL) {
    return `Guest sync exceeds max generations total (${GUEST_SYNC_MAX_GENERATIONS_TOTAL})`;
  }
  return null;
}

export interface ItemLocation {
  item: ProjectItem;
  project: Project;
}

interface ProjectsContextValue {
  /** Current page of user projects (API page or full guest bag). */
  projects: Project[];
  /**
   * Broader project catalog (up to 100) for ToolForm select, History join,
   * and slug/id lookups that may fall outside the current list page.
   * For guests this is the full local bag.
   */
  projectChoices: Project[];
  /** All drafts across catalog projects, newest first. */
  allItems: ItemLocation[];
  /** Newest N drafts (for the sidebar Recents list). */
  recents: (limit: number) => ItemLocation[];
  getProjectBySlug: (slug: string) => Project | undefined;
  getProjectById: (id: string) => Project | undefined;
  /** Create a project; returns its slug for navigation. */
  createProject: (name: string, desc: string) => Promise<string>;
  /** Edit a project's name/desc; returns its slug. */
  updateProject: (id: string, name: string, desc: string) => Promise<string>;
  /** Committed project context notes. */
  getNotes: (projectId: string) => string;
  /** Persist project context notes. */
  commitNotes: (projectId: string, value: string) => Promise<void>;
  /**
   * Attach a tool-run artifact to a guest local project.
   * No-op for authenticated users (API already links the generation).
   */
  addGeneratedItem: (projectId: string, item: ProjectItem) => void;
  /**
   * Rename a guest draft title in localStorage by generation slug.
   * Returns false if authenticated or the item is not in the guest bag.
   */
  updateGeneratedItemTitle: (slug: string, title: string) => boolean;
  /** True once auth + project list have settled (gate not-found on this). */
  hydrated: boolean;
  /** Current list page (1-based). */
  page: number;
  /** Total pages from API meta.pagination (always 1 for guests). */
  totalPages: number;
  /** Total project count. */
  totalProjects: number;
  /** True while a list page fetch is in flight (after first hydrate). */
  listLoading: boolean;
  /** Load another list page from the API. */
  setProjectsPage: (page: number) => void;
  /** Re-fetch the current projects list page (GET /v1/project). */
  refreshProjects: () => void;
  /**
   * Lazily load a broader catalog (up to 100) for ToolForm / History.
   * No-op if already fetched this session (or for guests).
   */
  ensureProjectCatalog: () => void;
  /**
   * Merge one API project into the catalog (e.g. after GET /v1/project/{slug})
   * so notes/lookups stay in sync without waiting for a list refetch.
   */
  cacheApiProject: (project: ApiProject) => void;
  /**
   * Localized error from the latest guest-bag → account sync attempt.
   * Null when idle or the last attempt succeeded / was skipped.
   */
  guestSyncError: string | null;
  /** Re-run guest-sync while the bag is still present after a failure. */
  retryGuestSync: () => void;
}
const ProjectsContext = createContext<ProjectsContextValue | null>(null);

function mapApiProject(
  p: ApiProject,
  genItems: ProjectItem[],
  generationCount: number,
): Project {
  return {
    id: String(p.id),
    slug: p.slug,
    name: p.name,
    desc: p.description ?? "",
    seed: false,
    items: genItems,
    generationCount,
  };
}

function mergeCatalog(prev: ApiProject[], incoming: ApiProject[]): ApiProject[] {
  const byId = new Map(prev.map((p) => [p.id, p]));
  for (const p of incoming) byId.set(p.id, p);
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/** Build project cards from API `generations[]` (cast modality on the way in). */
function attachItems(apiList: ApiProject[], locale?: string): Project[] {
  return apiList.map((p) => {
    const genItems = (p.generations ?? []).map((g) =>
      mapApiProjectGenerationSummary(g, locale),
    );
    const generationCount = Math.max(
      p.generationCount ?? 0,
      genItems.length,
    );
    return mapApiProject(p, genItems, generationCount);
  });
}

function guestRecordToProject(record: GuestProjectRecord): Project {
  return {
    id: record.id,
    slug: record.slug,
    name: record.name,
    desc: record.desc,
    seed: false,
    items: record.items,
    generationCount: record.items.length,
  };
}

function uniqueGuestSlug(name: string, existing: GuestProjectRecord[]): string {
  const base = slugify(name) || "project";
  let slug = base;
  let n = 2;
  const taken = new Set(existing.map((p) => p.slug));
  while (taken.has(slug)) {
    slug = `${base}-${n}`;
    n += 1;
  }
  return slug;
}

function makeDefaultGuestProject(name: string): GuestProjectRecord {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    slug: "default",
    name,
    desc: "",
    notes: "",
    items: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { isAuthenticated, hydrated: authHydrated } = useAuth();
  const { locale, hydrated: localeHydrated, t } = useLocale();
  const skipListPrefetch = shouldSkipProjectListPrefetch(pathname);

  const [guestBag, setGuestBag, guestStorageHydrated] =
    useLocalStorageState<GuestProjectsStorage>(
      GUEST_PROJECTS_STORAGE_KEY,
      EMPTY_GUEST_PROJECTS_STORAGE,
    );

  const [apiProjects, setApiProjects] = useState<ApiProject[]>([]);
  const [catalogApi, setCatalogApi] = useState<ApiProject[]>([]);
  const [apiPage, setApiPage] = useState(1);
  const [apiTotalPages, setApiTotalPages] = useState(1);
  const [apiTotalProjects, setApiTotalProjects] = useState(0);
  const [apiListLoading, setApiListLoading] = useState(false);
  /** Locale whose list page has finished loading (success or failure). */
  const [loadedListLocale, setLoadedListLocale] = useState<string | null>(null);
  const [guestSyncErrorState, setGuestSyncError] = useState<string | null>(null);
  /** False while an authenticated guest-sync attempt is in flight. */
  const [guestSyncSettledState, setGuestSyncSettled] = useState(true);

  // Guests have nothing to sync, so both are derived rather than reset in an effect.
  const guestSyncError = isAuthenticated ? guestSyncErrorState : null;
  const guestSyncSettled = !isAuthenticated || guestSyncSettledState;
  const [guestSyncRetryNonce, setGuestSyncRetryNonce] = useState(0);

  const pageRef = useRef(1);
  const catalogLoadedRef = useRef(false);
  const catalogInflightRef = useRef(false);
  /** True after a finished sync attempt for the current authenticated session. */
  const autoSyncDoneRef = useRef(false);
  const guestBagRef = useRef(guestBag);

  /**
   * Write the guest bag and advance the ref in the same tick. Guest mutations
   * read `guestBagRef.current` (see `createProject`), so the ref must not lag
   * behind a write that React has not re-rendered yet.
   */
  const commitGuestBag = useCallback(
    (next: GuestProjectsStorage) => {
      guestBagRef.current = next;
      setGuestBag(next);
    },
    [setGuestBag],
  );
  /** Dedupes concurrent GET /v1/project for the same page (e.g. Strict Mode). */
  const listInflightRef = useRef<{
    page: number;
    promise: Promise<void>;
  } | null>(null);

  const loadListPage = useCallback(
    async (nextPage: number, opts?: { isInitial?: boolean }) => {
      const existing = listInflightRef.current;
      if (existing?.page === nextPage) return existing.promise;

      const promise = (async () => {
        if (!opts?.isInitial) setApiListLoading(true);
        try {
          const { data, meta } = await fetchProjects({
            page: nextPage,
            perPage: LIST_PER_PAGE,
            locale,
          });
          const pagination = meta.pagination;
          setApiProjects(data);
          setApiPage(pagination?.page ?? nextPage);
          setApiTotalPages(Math.max(1, pagination?.totalPages ?? 1));
          setApiTotalProjects(pagination?.total ?? data.length);
          setCatalogApi((prev) => mergeCatalog(prev, data));
          setLoadedListLocale(locale);
        } finally {
          if (!opts?.isInitial) setApiListLoading(false);
          if (listInflightRef.current?.page === nextPage) {
            listInflightRef.current = null;
          }
        }
      })();

      listInflightRef.current = { page: nextPage, promise };
      return promise;
    },
    [locale],
  );

  const ensureProjectCatalog = useCallback(() => {
    if (!isAuthenticated || catalogLoadedRef.current || catalogInflightRef.current) {
      return;
    }
    catalogInflightRef.current = true;

    (async () => {
      try {
        const { data } = await fetchProjects({
          page: 1,
          perPage: CATALOG_PER_PAGE,
          locale,
        });
        setCatalogApi((prev) => mergeCatalog(prev, data));
        catalogLoadedRef.current = true;
      } catch (err) {
        console.error("Failed to load project catalog", err);
      } finally {
        catalogInflightRef.current = false;
      }
    })();
  }, [isAuthenticated, locale]);

  const cacheApiProject = useCallback((project: ApiProject) => {
    setCatalogApi((prev) => mergeCatalog(prev, [project]));
    setApiProjects((prev) =>
      prev.some((p) => p.id === project.id)
        ? prev.map((p) => (p.id === project.id ? project : p))
        : prev,
    );
  }, []);

  const retryGuestSync = useCallback(() => {
    autoSyncDoneRef.current = false;
    setGuestSyncError(null);
    setGuestSyncSettled(false);
    setGuestSyncRetryNonce((n) => n + 1);
  }, []);

  // After JWT: claim guest bag via POST /v1/project/guest-sync (or clear empty seed).
  useEffect(() => {
    if (!authHydrated || !guestStorageHydrated || !localeHydrated) return;

    if (!isAuthenticated) {
      // Nothing to claim while signed out; `guestSyncError` / `guestSyncSettled`
      // are derived above, so there is no state to reset here.
      autoSyncDoneRef.current = false;
      return;
    }

    if (autoSyncDoneRef.current) return;

    let cancelled = false;

    (async () => {
      setGuestSyncSettled(false);
      try {
        const bag = guestBagRef.current;
        if (!isGuestBagSyncable(bag, t.defaultProjectName)) {
          if (bag.projects.length > 0) {
            clearGuestProjects();
            if (!cancelled) commitGuestBag(EMPTY_GUEST_PROJECTS_STORAGE);
          }
          if (!cancelled) setGuestSyncError(null);
          return;
        }

        const payload = toGuestSyncPayload(bag);
        const limitError = validateGuestSyncPayload(payload);
        if (limitError) {
          console.error(limitError);
          if (!cancelled) setGuestSyncError(t.guestSyncLimitsExceeded);
          return;
        }

        await syncGuestProjects(payload);
        clearGuestProjects();
        if (!cancelled) {
          commitGuestBag(EMPTY_GUEST_PROJECTS_STORAGE);
          setGuestSyncError(null);
        }
      } catch (err) {
        console.error("Failed to sync guest projects", err);
        /* istanbul ignore next -- the effect is cancelled before this branch can be observed */
        if (cancelled) return;
        if (
          isApiError(err) &&
          err.code === "GUEST_GENERATION_NOT_CLAIMABLE"
        ) {
          setGuestSyncError(t.guestSyncNotClaimable);
        } else {
          setGuestSyncError(t.guestSyncFailed);
        }
      } finally {
        if (!cancelled) {
          autoSyncDoneRef.current = true;
          setGuestSyncSettled(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authHydrated,
    guestStorageHydrated,
    localeHydrated,
    isAuthenticated,
    guestSyncRetryNonce,
    commitGuestBag,
    t.defaultProjectName,
    t.guestSyncFailed,
    t.guestSyncLimitsExceeded,
    t.guestSyncNotClaimable,
  ]);

  // Mirror `page` / `guestBag` into refs from an effect — writing a ref while
  // rendering is what `react-hooks/refs` warns about, and mutations already keep
  // `guestBagRef` current through `commitGuestBag`.
  useEffect(() => {
    pageRef.current = apiPage;
  }, [apiPage]);

  useEffect(() => {
    guestBagRef.current = guestBag;
  }, [guestBag]);

  // Guest: seed the local bag with a Default project when it is empty.
  // Auth: load the first API list page after guest-sync settles
  // (skipped on routes that use show/catalog).
  useEffect(() => {
    if (!authHydrated || !localeHydrated) return;

    if (!isAuthenticated) {
      /* istanbul ignore next -- the storage hook reports hydrated on the first client render */
      if (!guestStorageHydrated) return;

      catalogLoadedRef.current = false;
      catalogInflightRef.current = false;

      if (guestBagRef.current.projects.length === 0) {
        commitGuestBag({
          version: GUEST_PROJECTS_STORAGE_VERSION,
          projects: [makeDefaultGuestProject(t.defaultProjectName)],
        });
      }
      return;
    }

    // Wait until guest-sync has finished for this auth session (ref is
    // synchronous; state alone can still be true from the prior guest session).
    if (!guestSyncSettled || !autoSyncDoneRef.current) return;
    if (skipListPrefetch) return;
    // Already have this locale's list in state — returning to /projects from a
    // route that skips the prefetch is not a reason to re-hit the API.
    if (loadedListLocale === locale) return;

    let cancelled = false;
    catalogLoadedRef.current = false;

    (async () => {
      try {
        await loadListPage(1, { isInitial: true });
      } catch (err) {
        console.error("Failed to load projects", err);
        /* istanbul ignore next -- the effect is cancelled before this branch can be observed */
        if (cancelled) return;
        setApiProjects([]);
        setCatalogApi([]);
        setApiTotalPages(1);
        setApiTotalProjects(0);
      } finally {
        // Mark the locale settled either way, so screens stop waiting.
        if (!cancelled) setLoadedListLocale(locale);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authHydrated,
    localeHydrated,
    isAuthenticated,
    guestStorageHydrated,
    guestSyncSettled,
    guestSyncRetryNonce,
    loadListPage,
    loadedListLocale,
    locale,
    skipListPrefetch,
    commitGuestBag,
    t.defaultProjectName,
  ]);

  const setProjectsPage = useCallback(
    (nextPage: number) => {
      if (!isAuthenticated) return;
      const clamped = Math.max(1, Math.min(nextPage, apiTotalPages));
      if (clamped === pageRef.current && apiProjects.length > 0) return;

      (async () => {
        try {
          await loadListPage(clamped);
        } catch (err) {
          console.error("Failed to load projects page", err);
        }
      })();
    },
    [isAuthenticated, apiTotalPages, apiProjects.length, loadListPage],
  );

  const refreshProjects = useCallback(() => {
    if (!isAuthenticated) return;

    // An explicit refresh must hit the network — otherwise the cached page
    // would be served straight back and this would be a silent no-op.
    invalidateProjects();

    (async () => {
      try {
        await loadListPage(pageRef.current);
      } catch (err) {
        console.error("Failed to refresh projects", err);
      }
    })();
  }, [isAuthenticated, loadListPage]);

  // Everything the tree reads is derived: guests own the local bag, signed-in
  // users the API page. No effect resets these when the session flips.
  const page = isAuthenticated ? apiPage : 1;
  const totalPages = isAuthenticated ? apiTotalPages : 1;
  const totalProjects = isAuthenticated
    ? apiTotalProjects
    : guestBag.projects.length;
  const listLoading = isAuthenticated && apiListLoading;

  /** True once the first list page settled (or was deliberately skipped). */
  const projectsLoaded = isAuthenticated
    ? skipListPrefetch || loadedListLocale === locale
    : guestStorageHydrated;

  const hydrated =
    authHydrated &&
    projectsLoaded &&
    (isAuthenticated || guestStorageHydrated);

  const guestProjects = useMemo<Project[]>(() => {
    return [...guestBag.projects]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .map(guestRecordToProject);
  }, [guestBag.projects]);

  // ── Current list page for the Projects grid ──
  const projects = useMemo<Project[]>(
    () =>
      isAuthenticated ? attachItems(apiProjects, locale) : guestProjects,
    [isAuthenticated, apiProjects, locale, guestProjects],
  );

  // ── Broader catalog for select / history / lookups ──
  const projectChoices = useMemo<Project[]>(
    () =>
      isAuthenticated ? attachItems(catalogApi, locale) : guestProjects,
    [isAuthenticated, catalogApi, locale, guestProjects],
  );
  const sortKey = (it: ProjectItem) => it.createdAt ?? dateSortKey(it.date);

  const allItems = useMemo<ItemLocation[]>(() => {
    const flat: ItemLocation[] = [];
    projectChoices.forEach((project) =>
      project.items.forEach((item) => flat.push({ item, project })),
    );
    return flat.sort((a, b) => sortKey(b.item) - sortKey(a.item));
  }, [projectChoices]);

  const recents = useCallback(
    (limit: number) => allItems.slice(0, limit),
    [allItems],
  );

  const getProjectBySlug = useCallback(
    (slug: string) =>
      projectChoices.find((p) => p.slug === slug) ??
      projects.find((p) => p.slug === slug),
    [projectChoices, projects],
  );
  const getProjectById = useCallback(
    (id: string) =>
      projectChoices.find((p) => p.id === id) ??
      projects.find((p) => p.id === id),
    [projectChoices, projects],
  );
  const createProject = useCallback(
    async (name: string, desc: string) => {
      if (!isAuthenticated) {
        // Derive from the ref, not inside a state updater: React only runs an
        // updater synchronously while the fiber is idle, so reading a value
        // assigned in there returns "" once the provider has re-rendered.
        const prev = guestBagRef.current;
        const now = Date.now();
        const record: GuestProjectRecord = {
          id: crypto.randomUUID(),
          slug: uniqueGuestSlug(name, prev.projects),
          name,
          desc,
          notes: "",
          items: [],
          createdAt: now,
          updatedAt: now,
        };
        commitGuestBag({
          version: GUEST_PROJECTS_STORAGE_VERSION,
          projects: [record, ...prev.projects],
        });
        return record.slug;
      }

      const body: { name: string; description?: string } = { name };
      if (desc) body.description = desc;
      const created = await storeProject(body, locale);
      setCatalogApi((prev) => mergeCatalog(prev, [created]));
      try {
        await loadListPage(1);
      } catch (err) {
        console.error("Failed to refresh projects after create", err);
        setApiProjects((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
        setApiPage(1);
      }
      return created.slug;
    },
    [commitGuestBag, isAuthenticated, loadListPage, locale],
  );

  const updateProject = useCallback(
    async (id: string, name: string, desc: string) => {
      if (!isAuthenticated) {
        const prev = guestBagRef.current;
        const current = prev.projects.find((p) => p.id === id);
        if (!current) return "";
        commitGuestBag({
          version: GUEST_PROJECTS_STORAGE_VERSION,
          projects: prev.projects.map((p) =>
            p.id === id ? { ...p, name, desc, updatedAt: Date.now() } : p,
          ),
        });
        return current.slug;
      }

      const current =
        catalogApi.find((p) => String(p.id) === id) ??
        apiProjects.find((p) => String(p.id) === id);
      if (!current) return "";
      const updated = await patchProject(
        current.slug,
        {
          name,
          description: desc,
        },
        locale,
      );
      setApiProjects((prev) =>
        prev.map((p) => (p.id === updated.id ? updated : p)),
      );
      setCatalogApi((prev) => mergeCatalog(prev, [updated]));
      return updated.slug;
    },
    [apiProjects, catalogApi, commitGuestBag, isAuthenticated, locale],
  );

  const getNotes = useCallback(
    (projectId: string) => {
      if (!isAuthenticated) {
        return (
          guestBag.projects.find((p) => p.id === projectId)?.notes ?? ""
        );
      }
      /* istanbul ignore next -- the list fallback never fires: the catalog is a superset */
      const api =
        catalogApi.find((p) => String(p.id) === projectId) ??
        apiProjects.find((p) => String(p.id) === projectId);
      return api?.notes ?? "";
    },
    [apiProjects, catalogApi, guestBag.projects, isAuthenticated],
  );

  const commitNotes = useCallback(
    async (projectId: string, value: string) => {
      if (!isAuthenticated) {
        const prev = guestBagRef.current;
        if (!prev.projects.some((p) => p.id === projectId)) {
          throw new ApiError(404, "NOT_FOUND", "Project not found.");
        }
        commitGuestBag({
          version: GUEST_PROJECTS_STORAGE_VERSION,
          projects: prev.projects.map((p) =>
            p.id === projectId ? { ...p, notes: value, updatedAt: Date.now() } : p,
          ),
        });
        return;
      }

      const current =
        catalogApi.find((p) => String(p.id) === projectId) ??
        apiProjects.find((p) => String(p.id) === projectId);
      if (!current) throw new ApiError(404, "NOT_FOUND", "Project not found.");
      const updated = await patchProject(
        current.slug,
        { notes: value },
        locale,
      );
      setApiProjects((prev) =>
        prev.map((p) => (p.id === updated.id ? updated : p)),
      );
      setCatalogApi((prev) => mergeCatalog(prev, [updated]));
    },
    [
      apiProjects,
      catalogApi,
      commitGuestBag,
      isAuthenticated,
      locale,
    ],
  );

  const addGeneratedItem = useCallback(
    (projectId: string, item: ProjectItem) => {
      if (isAuthenticated) {
        console.error(
          "addGeneratedItem is guest-only; authenticated runs link via the API",
        );
        return;
      }
      const prev = guestBagRef.current;
      if (!prev.projects.some((p) => p.id === projectId)) {
        console.error(
          `addGeneratedItem: guest project "${projectId}" not found`,
        );
        return;
      }
      commitGuestBag({
        version: GUEST_PROJECTS_STORAGE_VERSION,
        projects: prev.projects.map((p) =>
          p.id === projectId
            ? {
                ...p,
                items: [
                  item,
                  ...p.items.filter((existing) => existing.slug !== item.slug),
                ],
                updatedAt: Date.now(),
              }
            : p,
        ),
      });
    },
    [commitGuestBag, isAuthenticated],
  );

  const updateGeneratedItemTitle = useCallback(
    (slug: string, title: string): boolean => {
      if (isAuthenticated) {
        console.error(
          "updateGeneratedItemTitle is guest-only; authenticated drafts use the API",
        );
        return false;
      }
      const prev = guestBagRef.current;
      const hasItem = prev.projects.some((p) =>
        p.items.some((item) => item.slug === slug),
      );
      if (!hasItem) {
        console.error(
          `updateGeneratedItemTitle: guest generation "${slug}" not found`,
        );
        return false;
      }
      const now = Date.now();
      commitGuestBag({
        version: GUEST_PROJECTS_STORAGE_VERSION,
        projects: prev.projects.map((p) => {
          if (!p.items.some((item) => item.slug === slug)) return p;
          return {
            ...p,
            items: p.items.map((item) =>
              item.slug === slug ? { ...item, name: title } : item,
            ),
            updatedAt: now,
          };
        }),
      });
      return true;
    },
    [commitGuestBag, isAuthenticated],
  );

  const value = useMemo<ProjectsContextValue>(
    () => ({
      projects,
      projectChoices,
      allItems,
      recents,
      getProjectBySlug,
      getProjectById,
      createProject,
      updateProject,
      getNotes,
      commitNotes,
      addGeneratedItem,
      updateGeneratedItemTitle,
      hydrated,
      page,
      totalPages,
      totalProjects,
      listLoading,
      setProjectsPage,
      refreshProjects,
      ensureProjectCatalog,
      cacheApiProject,
      guestSyncError,
      retryGuestSync,
    }),
    [
      projects,
      projectChoices,
      allItems,
      recents,
      getProjectBySlug,
      getProjectById,
      createProject,
      updateProject,
      getNotes,
      commitNotes,
      addGeneratedItem,
      updateGeneratedItemTitle,
      hydrated,
      page,
      totalPages,
      totalProjects,
      listLoading,
      setProjectsPage,
      refreshProjects,
      ensureProjectCatalog,
      cacheApiProject,
      guestSyncError,
      retryGuestSync,
    ],
  );
  return (
    <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>
  );
}

export function useProjects(): ProjectsContextValue {
  const ctx = useContext(ProjectsContext);
  if (!ctx) throw new Error("useProjects must be used within <ProjectsProvider>");
  return ctx;
}
